import IosPwaPrompt from 'react-ios-pwa-prompt';
import React from 'react';

import { AndroidPwaPrompt } from '../../components/AndroidPwaPrompt';
import createContextFactory from '../createContextFactory';
import { useIntl } from '../TranslationsProvider';
import { useIsMobileShell } from '../../hooks';
import messages from './messages';

export interface BeforeInstallPromptEvent extends Event {
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

export interface PwaInstallPromptContextType {
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  deferredPrompt: BeforeInstallPromptEvent | null;
  isVisible: boolean;
}
const PwaInstallPromptContext = createContextFactory({
  devToolsName: 'PwaInstallPromptContext',
  initialValue: {} as PwaInstallPromptContextType,
});

// Chrome offers the install on every load of a not-installed app; the prompt
// answers that offer once per device.
const ANDROID_PROMPT_SHOWN_KEY = 'mfwAndroidPwaPromptShown';

function claimAndroidPromptShow() {
  try {
    if (localStorage.getItem(ANDROID_PROMPT_SHOWN_KEY)) return false;
    localStorage.setItem(ANDROID_PROMPT_SHOWN_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

export interface PwaInstallPromptProviderProps {
  children?: React.ReactNode;
  appIconPath?: string;
  /**
   * Whether the prompt may be SHOWN now. Chrome's install offer is captured
   * from mount regardless — it arrives once per page load, often before the
   * account has loaded — and held until this turns true.
   */
  canPresent: boolean;
}
export const PwaInstallPromptProvider = ({
  appIconPath,
  canPresent,
  children,
}: PwaInstallPromptProviderProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobileShell();

  const [isVisible, setVisible] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] = React.useState<any>(null);

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  React.useEffect(() => {
    if (!isMobile || isIos) return;
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
  }, [isMobile, isIos]);

  // The device's one show is spent when the prompt is SHOWN, not when the
  // offer is captured — an offer held on the sign-in page has not been seen.
  React.useEffect(() => {
    if (canPresent && deferredPrompt && claimAndroidPromptShow()) {
      setVisible(true);
    }
  }, [canPresent, deferredPrompt]);

  const providedValue = React.useMemo(
    () => ({ deferredPrompt, isVisible, setVisible }),
    [isVisible, setVisible, deferredPrompt],
  );

  return (
    <PwaInstallPromptContext.Provider value={providedValue}>
      {children}
      {isIos ? (
        // No `isShown`: it overrides the library's own visit counter, and the
        // sheet then came back on every page load. Not mounted before
        // `canPresent` either — mounting is what counts a visit.
        isMobile &&
        canPresent && (
          <IosPwaPrompt
            copyAddToHomeScreenStep={formatMessage(
              messages.stepAddToHomeScreen,
            )}
            copyDescription={formatMessage(messages.installDescription)}
            copyShareStep={formatMessage(messages.stepShare)}
            copyTitle={formatMessage(messages.installTitle)}
            appIconPath={appIconPath}
            promptOnVisit={1}
            timesToShow={1}
          />
        )
      ) : (
        <AndroidPwaPrompt
          closeLabel={formatMessage(messages.close)}
          description={formatMessage(messages.installDescription)}
          installLabel={formatMessage(messages.installButton)}
          title={formatMessage(messages.installTitle)}
          appIconPath={appIconPath}
          isShown={isVisible && canPresent}
          promptOnVisit={1}
          timesToShow={1}
        />
      )}
    </PwaInstallPromptContext.Provider>
  );
};

export function usePwaInstallPromptContext() {
  return PwaInstallPromptContext.useContext();
}
